import { TrendingUp, TrendingDown, AlertTriangle, RefreshCw } from 'lucide-react';
import { useStockPrice } from '@/hooks/useStockPrice';
import { Button } from '@/components/ui/button';

interface StockPriceDisplayProps {
  symbol: string;
  exchange?: string;
  /** Called when the user wants to enter the price manually after an API failure. */
  onManualEntry?: () => void;
}

/**
 * Displays real-time stock price data for a given symbol.
 * Falls back to a warning with a manual-entry option when the API is unavailable.
 */
export default function StockPriceDisplay({ symbol, exchange, onManualEntry }: StockPriceDisplayProps) {
  const { data, isLoading, error, refetch } = useStockPrice(symbol, exchange);

  if (isLoading) {
    return <span className="text-xs text-muted-foreground">Loading price...</span>;
  }

  if (error || !data) {
    console.error('Stock price API unavailable for', symbol, error);
    return (
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
          Price unavailable
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs"
          onClick={() => refetch()}
          aria-label={`Retry fetching price for ${symbol}`}
        >
          <RefreshCw className="mr-1 h-3 w-3" aria-hidden="true" />
          Retry
        </Button>
        {onManualEntry && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={onManualEntry}
            aria-label={`Enter price manually for ${symbol}`}
          >
            Enter manually
          </Button>
        )}
      </div>
    );
  }

  const isUp = data.change >= 0;
  return (
    <div className="flex items-center gap-1 text-xs">
      <span className="font-mono">
        {data.currency ?? '$'}{data.price.toFixed(2)}
      </span>
      <span className={isUp ? 'text-emerald-600' : 'text-red-600'}>
        {isUp ? '+' : ''}{data.changePercent.toFixed(2)}%
      </span>
      {isUp ? (
        <TrendingUp className="h-3 w-3 text-emerald-600" aria-hidden="true" />
      ) : (
        <TrendingDown className="h-3 w-3 text-red-600" aria-hidden="true" />
      )}
    </div>
  );
}
